import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, inject, watch, computed } from 'vue'
import type { Ref } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'

interface LimitItem {
  limit: number | string
  caption: string
}

interface Pagination {
  limit: string | number
  offset: string | number
  total: string | number
}



const PAGE_SHIFT = 1;
const __default__ = {}

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  __name: 'UiPagination',
  props: {
    scrollPaginate: { type: Boolean, default: false },
    showMoreBtn: { type: Boolean, default: false },
    startPage: { type: [Boolean, Number], default: false },
    shift: { default: PAGE_SHIFT },
    total: {},
    limit: {},
    offset: {},
    extra: { default: 2 },
    url: { default: '' },
    isQueryPagination: { type: Boolean }
  },
  emits: ['input', 'loadMore'],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();


const props = __props


const emit = __emit

function changeLimit({ limit }: LimitItem) {
  emit('input', { limit })
}

const range = ref({
  min: 0,
  max: Infinity,
})
const gaps = ref<{
  left: boolean | number
  right: boolean | number
}>({
  left: false,
  right: false
})
const fills = ref<{
  left: number[]
  right: number[]
}>({
  left: [],
  right: [],
})


const hasNext = computed(() => (+props?.offset || 0) + +props?.limit < +props?.total)

const hasPrevious = computed(() => 0 < (+props?.offset || 0))


watch(props, () => { recalculateRange() })

onMounted(() => {
  if (props.total) {
    recalculateRange()
  }
})

function getPrevious() {
  return getPageNumber() - props.shift;
}

function getNext() {
  return getPageNumber() + props.shift;
}

function getPageNumber(offset = props?.offset || 0, shift = props.shift) {
  return Math.floor(Number(offset) / Number(props?.limit)) + Number(shift);
}

function getPageRange(distance: null | number | undefined = null, current = getPageNumber()) {
  const min = 1;
  const max = Math.max(min, Math.ceil(props?.total / props?.limit));

  if (null === distance || Infinity === distance) {
    return [min, max];
  }

  return [
    Math.max(min, current - distance),
    Math.min(max, current + distance),
  ];
}

function recalculateRange() {
  const [min, max] = getPageRange();
  const current = getPageNumber();
  const [start, end] = getPageRange(props.extra, current);
  // Some javascript magic going on here:
  // Filling up array with page numbers.
  // const newRange = new Array(...new Array(end - start + 1))
  //   .map((x, i) => i + start);
  // eslint-disable-next-line prefer-spread
  const newRange: number[] = Array
    .apply(null, { length: end - start + 1 })
    .map((x, i) => i + start);
  const currentIndex = newRange.indexOf(current);

  if (-1 !== currentIndex) {
    fills.value.left = newRange.slice(0, currentIndex).filter(x => x !== min);
    fills.value.right = newRange.slice(currentIndex + 1).filter(x => x !== max);
  } else {
    fills.value.left = [];
    fills.value.right = [];
  }

  gaps.value.left = (
    fills.value.left.length &&
    fills.value.left[0] !== min + 1
  );
  gaps.value.right = (
    fills.value.right.length &&
    fills.value.right[fills.value.right.length - 1] !== max - 1
  );
  range.value.min = min;
  range.value.max = max;
}

function getParameters(number: null | number = null) {
  let page = 1;

  if (null !== number) {
    const [min, max] = getPageRange(Infinity, number);

    page = Math.min(max, Math.max(min, number));
  } else {
    page = getPageNumber();
  }

  return {
    offset: props?.limit * (page - props.shift),
    limit: props?.limit,
  };
}

// scrollable pagination
const observerTarget = ref(null)

const { stop } = useIntersectionObserver(
  observerTarget,
  ([{ isIntersecting }]: IntersectionObserverEntry[]) => {
    if (isIntersecting) {
      loadMore();
    }
  },
  { rootMargin: '0px 0px 400px 0px' }
)


function loadMore() {
  emit('loadMore', getParameters(getPageNumber() + props.shift));
}

function select(number: number) {
  emit('input', getParameters(number));
}

function next() {
  emit('input', getParameters(getPageNumber() + props.shift));
}

function previous() {
  emit('input', getParameters(getPageNumber() - props.shift));
}

function getUrl(page) {
  const searchParams = new URLSearchParams(window.location.search)
  if (props.isQueryPagination) {
    const offset = page - props.shift ? props?.limit * (page - props.shift) : 0
    if (offset && Number.isFinite(offset)) {
      searchParams.set('offset', `${offset}`)
    } else {
      searchParams.delete('offset')
    }
    searchParams.set('limit', `${props?.limit}`)
    return `${props.url}?${searchParams.toString()}`
  }
  return searchParams.toString() ? `${props.url}page/${page}/?${searchParams.toString()}` : `${props.url}page/${page}/`
}

const __returned__ = { PAGE_SHIFT, props, emit, changeLimit, range, gaps, fills, hasNext, hasPrevious, getPrevious, getNext, getPageNumber, getPageRange, recalculateRange, getParameters, observerTarget, stop, loadMore, select, next, previous, getUrl, ref, onMounted, inject, watch, computed, get useIntersectionObserver() { return useIntersectionObserver } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})